import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  StorefrontPromotionAttribution,
  StorefrontQuote,
} from "@emynor/storefront-sdk";
import { storefrontSdk } from "./sdk";
import type { Cart, CartLine, Localized, OrderTypeSelection } from "./types";
import {
  buildQuoteInput,
  calculateLineTotal,
  cartWithQuotePrices,
  quoteInputFingerprint,
  validateCartAddition,
} from "./cart-model";
import {
  customerAvailabilityRefreshTargets,
  customerErrorDetails,
  customerErrorItemId,
  customerErrorUsesDialog,
  type CustomerErrorDetails,
} from "./customer-error";
import { useI18n } from "./i18n";
import { useCustomerErrorViewer } from "@/components/customer-error-viewer";
import { storePromotionAttribution } from "./promotion-attribution";
import { recoverUnfinishedCart } from "./unfinished-checkout-cart";
import { featureEnabled, useStorefront } from "./storefront";

const CART_KEY = "emynor.storefront.cart.v2";
const emptyCart: Cart = { lines: [] };

interface CartValue {
  cart: Cart;
  hydrated: boolean;
  lineCount: number;
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  discount: number;
  tax: number;
  total: number;
  quote: StorefrontQuote | null;
  quotePending: boolean;
  quoteError: CustomerErrorDetails | null;
  addLine: (line: Omit<CartLine, "id">) => Promise<void>;
  setQuantity: (lineId: string, quantity: number) => void;
  removeLine: (lineId: string) => void;
  clear: () => void;
  restoreUnfinished: (saved: Cart, replaceCurrent?: boolean) => void;
  setOrderType: (selection: OrderTypeSelection) => void;
  setPromotionCode: (promotionCode: string) => void;
  setPromotionAttribution: (
    attribution: StorefrontPromotionAttribution,
  ) => void;
  clearPromotionAttribution: () => void;
  setLoyaltyPoints: (points: number | null) => void;
  refreshQuote: (cartOverride?: Cart) => Promise<StorefrontQuote>;
}

const CartContext = createContext<CartValue | null>(null);
export function CartProvider({ children }: { children: ReactNode }) {
  const { t, lang } = useI18n();
  const storefront = useStorefront();
  const loyaltyAvailable =
    storefront.mode !== "preview" && featureEnabled(storefront, "loyalty");
  const loyaltyAvailableAtHydration = useRef(loyaltyAvailable);
  const queryClient = useQueryClient();
  const { showError } = useCustomerErrorViewer();
  const [cart, setCart] = useState<Cart>(emptyCart);
  const [hydrated, setHydrated] = useState(false);
  const [quote, setQuote] = useState<StorefrontQuote | null>(null);
  const [quotePending, setQuotePending] = useState(false);
  const [quoteError, setQuoteError] = useState<CustomerErrorDetails | null>(
    null,
  );
  const quoteSequence = useRef(0);
  const authenticatedRef = useRef(storefrontSdk.authState.authenticated);
  const quoteAbortController = useRef<AbortController | null>(null);
  const cartRef = useRef(cart);
  const settledCartRef = useRef<Cart | null>(null);
  const quotedCartFingerprintRef = useRef<string | null>(null);

  const updateCart = useCallback((updater: (current: Cart) => Cart) => {
    setCart((current) => {
      const next = updater(current);
      cartRef.current = next;
      return next;
    });
  }, []);

  const refreshAvailability = useCallback(
    (error: unknown) => {
      const targets = customerAvailabilityRefreshTargets(error);
      void Promise.all(
        targets.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey: [queryKey] }),
        ),
      );
      return targets.length > 0;
    },
    [queryClient],
  );

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CART_KEY);
      if (raw) {
        const candidate = { ...emptyCart, ...(JSON.parse(raw) as Cart) };
        delete (candidate as Cart & { redeemLoyaltyReward?: boolean })
          .redeemLoyaltyReward;
        if (!loyaltyAvailableAtHydration.current)
          delete candidate.redeemLoyaltyPoints;
        const stored = normalizeStoredPromotionAttribution(candidate);
        cartRef.current = stored;
        setCart(stored);
      }
    } catch {
      window.localStorage.removeItem(CART_KEY);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated || loyaltyAvailable) return;
    updateCart((current) =>
      current.redeemLoyaltyPoints === undefined
        ? current
        : withoutLoyaltyPoints(current),
    );
  }, [hydrated, loyaltyAvailable, updateCart]);

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(CART_KEY, JSON.stringify(cart));
    storePromotionAttribution(cart.promotionAttribution);
  }, [cart, hydrated]);

  const refreshQuote = useCallback(
    async function refreshQuote(cartOverride?: Cart) {
      const input = buildQuoteInput(cartOverride ?? cart);
      if (!input)
        throw new Error("Choose an order type and add at least one item");
      const sequence = ++quoteSequence.current;
      quoteAbortController.current?.abort();
      const controller =
        typeof AbortController === "function" ? new AbortController() : null;
      quoteAbortController.current = controller;
      setQuotePending(true);
      setQuoteError(null);
      try {
        const result = await storefrontSdk.quote(
          input,
          controller ? { signal: controller.signal } : undefined,
        );
        if (sequence === quoteSequence.current) {
          setQuote(result);
          updateCart((current) => {
            const priced = cartWithQuotePrices(current, result);
            if (
              !result.promotionAttributionToken ||
              !result.promotionAttributionExpiresAt
            ) {
              return priced;
            }
            const applied = result.promotions[0];
            const attributedSource =
              applied?.source === "link"
                ? "link"
                : applied?.source === "code"
                  ? "code"
                  : null;
            const attributedApplication = attributedSource ? applied : null;
            const promotionAttribution = {
              token: result.promotionAttributionToken!,
              expiresAt: result.promotionAttributionExpiresAt!,
              promotionId:
                attributedApplication?.id ??
                priced.promotionAttribution?.promotionId ??
                "",
              promotionName: attributedApplication
                ? localized(attributedApplication.name)
                : (priced.promotionAttribution?.promotionName ?? {
                    ar: "",
                    en: "",
                  }),
              source:
                attributedSource ??
                priced.promotionAttribution?.source ??
                "link",
            };
            return {
              ...priced,
              promotionAttribution,
              ...(promotionAttribution.source === "link" &&
              !priced.linkedPromotionAttribution
                ? {
                    linkedPromotionAttribution: {
                      ...promotionAttribution,
                      source: "link" as const,
                    },
                  }
                : {}),
            };
          });
        }
        return result;
      } catch (error) {
        if (sequence === quoteSequence.current) {
          if (
            error &&
            typeof error === "object" &&
            "code" in error &&
            error.code === "promotion_attribution_unavailable" &&
            linkedPromotionAttribution(cartOverride ?? cart)
          ) {
            updateCart((current) => withoutLinkedPromotionAttribution(current));
          }
          const itemName = cartErrorItemName(
            error,
            (cartOverride ?? cart).lines,
            lang,
          );
          const details = customerErrorDetails(error, t, { itemName });
          setQuote(null);
          setQuoteError(details);
          const canRefresh = refreshAvailability(error);
          if (customerErrorUsesDialog(error)) {
            showError(error, {
              details,
              ...(canRefresh
                ? {
                    actionLabel: t("common.retry"),
                    onAction: () =>
                      void refreshQuote(cartOverride).catch(() => undefined),
                  }
                : {}),
            });
          }
        }
        throw error;
      } finally {
        if (sequence === quoteSequence.current) setQuotePending(false);
        if (quoteAbortController.current === controller) {
          quoteAbortController.current = null;
        }
      }
    },
    [cart, lang, refreshAvailability, showError, t, updateCart],
  );

  useEffect(
    () =>
      storefrontSdk.subscribeAuth((state) => {
        if (authenticatedRef.current === state.authenticated) return;
        authenticatedRef.current = state.authenticated;
        let current = cartRef.current;
        if (!state.authenticated && current.redeemLoyaltyPoints) {
          const { redeemLoyaltyPoints: _redeemLoyaltyPoints, ...rest } =
            current;
          current = rest;
          updateCart(() => rest);
        }
        if (!hydrated || !quoteInputFingerprint(current)) return;
        quotedCartFingerprintRef.current = null;
        void refreshQuote(current).catch(() => undefined);
      }),
    [hydrated, refreshQuote, updateCart],
  );

  useEffect(
    () => () => {
      quoteSequence.current += 1;
      quoteAbortController.current?.abort();
    },
    [],
  );

  useEffect(() => {
    const fingerprint = quoteInputFingerprint(cart);
    if (settledCartRef.current === cart) {
      settledCartRef.current = null;
      quotedCartFingerprintRef.current = fingerprint;
      return;
    }
    if (!hydrated || !fingerprint) {
      quoteSequence.current += 1;
      quoteAbortController.current?.abort();
      quoteAbortController.current = null;
      quotedCartFingerprintRef.current = null;
      setQuote(null);
      setQuoteError(null);
      setQuotePending(false);
      return;
    }
    if (quotedCartFingerprintRef.current === fingerprint) return;
    quoteSequence.current += 1;
    quoteAbortController.current?.abort();
    quoteAbortController.current = null;
    // Keep the last confirmed totals visible until the replacement quote settles.
    setQuoteError(null);
    setQuotePending(true);
    const timeout = window.setTimeout(() => {
      quotedCartFingerprintRef.current = fingerprint;
      void refreshQuote().catch(() => undefined);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [cart, hydrated, refreshQuote]);

  useEffect(() => {
    if (!quote?.nextSaleChangeAt || !hydrated) return;
    const transition = Date.parse(quote.nextSaleChangeAt);
    if (!Number.isFinite(transition)) return;
    const timeout = window.setTimeout(
      () => {
        if (quoteInputFingerprint(cartRef.current)) {
          void refreshQuote(cartRef.current).catch(() => undefined);
        }
      },
      Math.min(2_147_483_647, Math.max(50, transition - Date.now() + 50)),
    );
    return () => window.clearTimeout(timeout);
  }, [quote?.nextSaleChangeAt, hydrated, refreshQuote]);

  const addLine = useCallback(
    async (line: Omit<CartLine, "id">) => {
      const sequence = ++quoteSequence.current;
      quoteAbortController.current?.abort();
      quoteAbortController.current = null;
      setQuotePending(true);
      try {
        const result = await validateCartAddition(
          cartRef.current,
          line,
          (input) => storefrontSdk.quote(input),
        );
        if (sequence !== quoteSequence.current)
          throw new Error("Cart changed while adding the item");
        const pricedCart = result.quote
          ? cartWithQuotePrices(result.cart, result.quote)
          : result.cart;
        settledCartRef.current = pricedCart;
        quotedCartFingerprintRef.current = quoteInputFingerprint(pricedCart);
        updateCart(() => pricedCart);
        setQuote(result.quote);
        setQuoteError(
          result.quoteError ? customerErrorDetails(result.quoteError, t) : null,
        );
      } catch (error) {
        if (sequence !== quoteSequence.current) throw error;
        const itemName = cartErrorItemName(error, [line], lang);
        const details = customerErrorDetails(error, t, { itemName });
        const canRefresh = refreshAvailability(error);
        if (customerErrorUsesDialog(error)) {
          showError(error, {
            details,
            ...(canRefresh
              ? {
                  actionLabel: t("common.retry"),
                  onAction: () => refreshAvailability(error),
                }
              : {}),
          });
        }
        throw error;
      } finally {
        if (sequence === quoteSequence.current) setQuotePending(false);
      }
    },
    [lang, refreshAvailability, showError, t, updateCart],
  );

  const setQuantity = useCallback(
    (lineId: string, quantity: number) => {
      updateCart((current) => ({
        ...current,
        lines:
          quantity <= 0
            ? current.lines.filter((line) => line.id !== lineId)
            : current.lines.map((line) =>
                line.id === lineId ? { ...line, quantity } : line,
              ),
      }));
    },
    [updateCart],
  );
  const removeLine = useCallback(
    (lineId: string) =>
      updateCart((current) => ({
        ...current,
        lines: current.lines.filter((line) => line.id !== lineId),
      })),
    [updateCart],
  );
  const clear = useCallback(
    () =>
      updateCart((current) => {
        const {
          promotionCode: _promotionCode,
          promotionAttribution: _promotionAttribution,
          linkedPromotionAttribution: _linkedPromotionAttribution,
          redeemLoyaltyPoints: _redeemLoyaltyPoints,
          ...rest
        } = current;
        return { ...rest, lines: [] };
      }),
    [updateCart],
  );
  const restoreUnfinished = useCallback(
    (saved: Cart, replaceCurrent = false) =>
      updateCart((current) =>
        loyaltyAvailable
          ? replaceCurrent
            ? saved
            : recoverUnfinishedCart(current, saved)
          : withoutLoyaltyPoints(
              replaceCurrent ? saved : recoverUnfinishedCart(current, saved),
            ),
      ),
    [loyaltyAvailable, updateCart],
  );
  const setOrderType = useCallback(
    (selection: OrderTypeSelection) =>
      updateCart((current) => ({ ...current, orderType: selection })),
    [updateCart],
  );
  const setPromotionCode = useCallback(
    (promotionCode: string) =>
      updateCart((current) => {
        const value = promotionCode.trim();
        if (value) {
          const linked = linkedPromotionAttribution(current);
          const { promotionAttribution: _promotionAttribution, ...rest } =
            current;
          return {
            ...rest,
            promotionCode: value,
            ...(linked ? { promotionAttribution: linked } : {}),
          };
        }
        const { promotionCode: _promotionCode, ...withoutCode } = current;
        return withoutCode.promotionAttribution?.source === "code"
          ? restoreLinkedPromotionAttribution(withoutCode)
          : withoutCode;
      }),
    [updateCart],
  );
  const setPromotionAttribution = useCallback(
    (attribution: StorefrontPromotionAttribution) =>
      updateCart((current) => ({
        ...current,
        promotionAttribution: linkAttribution(attribution),
        linkedPromotionAttribution: linkAttribution(attribution),
      })),
    [updateCart],
  );
  const clearPromotionAttribution = useCallback(
    () => updateCart((current) => withoutLinkedPromotionAttribution(current)),
    [updateCart],
  );
  const setLoyaltyPoints = useCallback(
    (points: number | null) =>
      updateCart((current) => {
        if (!loyaltyAvailable && points !== null) return current;
        if (points !== null) return { ...current, redeemLoyaltyPoints: points };
        const { redeemLoyaltyPoints: _redeemLoyaltyPoints, ...rest } = current;
        return rest;
      }),
    [loyaltyAvailable, updateCart],
  );

  const estimatedSubtotal = useMemo(
    () => cart.lines.reduce((sum, line) => sum + calculateLineTotal(line), 0),
    [cart.lines],
  );
  const value = useMemo<CartValue>(
    () => ({
      cart,
      hydrated,
      lineCount: cart.lines.reduce((sum, line) => sum + line.quantity, 0),
      subtotal: quote?.pricing.subtotalMinor ?? estimatedSubtotal,
      deliveryFee: quote?.pricing.deliveryFeeMinor ?? 0,
      serviceFee: quote?.pricing.serviceFeeMinor ?? 0,
      discount: quote?.pricing.discountMinor ?? 0,
      tax: quote?.pricing.taxMinor ?? 0,
      total: quote?.pricing.totalMinor ?? estimatedSubtotal,
      quote,
      quotePending,
      quoteError,
      addLine,
      setQuantity,
      removeLine,
      clear,
      restoreUnfinished,
      setOrderType,
      setPromotionCode,
      setPromotionAttribution,
      clearPromotionAttribution,
      setLoyaltyPoints,
      refreshQuote,
    }),
    [
      cart,
      hydrated,
      quote,
      quotePending,
      quoteError,
      estimatedSubtotal,
      addLine,
      setQuantity,
      removeLine,
      clear,
      restoreUnfinished,
      setOrderType,
      setPromotionCode,
      setPromotionAttribution,
      clearPromotionAttribution,
      setLoyaltyPoints,
      refreshQuote,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

function cartErrorItemName(
  error: unknown,
  lines: ReadonlyArray<Pick<CartLine, "itemId" | "name">>,
  lang: "ar" | "en",
): string | undefined {
  const itemId = customerErrorItemId(error);
  const line = lines.find((candidate) => candidate.itemId === itemId);
  if (!line) return undefined;
  return line.name[lang] || line.name.en || line.name.ar || undefined;
}

export function useCart(): CartValue {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used inside CartProvider");
  return context;
}

export const calcLineTotal = calculateLineTotal;

function linkAttribution(attribution: StorefrontPromotionAttribution) {
  return {
    token: attribution.attributionToken,
    expiresAt: attribution.expiresAt,
    promotionId: attribution.promotion.id,
    promotionName: localized(attribution.promotion.name),
    source: "link" as const,
  };
}

function linkedPromotionAttribution(
  cart: Cart,
): Cart["linkedPromotionAttribution"] {
  return (
    cart.linkedPromotionAttribution ??
    (cart.promotionAttribution?.source === "link"
      ? { ...cart.promotionAttribution, source: "link" }
      : undefined)
  );
}

function withoutLinkedPromotionAttribution(cart: Cart): Cart {
  const {
    linkedPromotionAttribution: _linkedAttribution,
    promotionAttribution,
    ...rest
  } = cart;
  if (!promotionAttribution || promotionAttribution.source === "link")
    return rest;
  return { ...rest, promotionAttribution };
}

function withoutLoyaltyPoints(cart: Cart): Cart {
  if (cart.redeemLoyaltyPoints === undefined) return cart;
  const { redeemLoyaltyPoints: _redeemLoyaltyPoints, ...rest } = cart;
  return rest;
}

function restoreLinkedPromotionAttribution(cart: Cart): Cart {
  const linked = linkedPromotionAttribution(cart);
  const { promotionAttribution: _attribution, ...rest } = cart;
  return linked ? { ...rest, promotionAttribution: linked } : rest;
}

function normalizeStoredPromotionAttribution(cart: Cart): Cart {
  const now = Date.now();
  const active =
    cart.promotionAttribution &&
    new Date(cart.promotionAttribution.expiresAt).getTime() > now
      ? cart.promotionAttribution
      : undefined;
  const linkedCandidate: Cart["linkedPromotionAttribution"] =
    cart.linkedPromotionAttribution ??
    (active?.source === "link" ? { ...active, source: "link" } : undefined);
  const linked =
    linkedCandidate && new Date(linkedCandidate.expiresAt).getTime() > now
      ? linkedCandidate
      : undefined;
  const {
    promotionAttribution: _promotionAttribution,
    linkedPromotionAttribution: _linkedPromotionAttribution,
    ...rest
  } = cart;
  return {
    ...rest,
    ...(active ? { promotionAttribution: active } : {}),
    ...(linked ? { linkedPromotionAttribution: linked } : {}),
  };
}

function localized(value: Record<string, string>): Localized {
  return { ar: value["ar"] ?? "", en: value["en"] ?? "" };
}
